/* ----------------------------------------------------------------------------
 * wd-product-sticky-cta.js
 * ----------------------------------------------------------------------------
 * Mobile-only sticky bottom CTA bar for product pages.
 *
 * What it does:
 *   1. Finds the main "Buy" button inside the inline product form
 *      (`.buyBtnForm` or its variants).
 *   2. Watches it with IntersectionObserver — when it scrolls out of view,
 *      slides a fixed bottom bar up containing the price + a clone Buy button.
 *      When the inline button is back in view, the bar slides down.
 *   3. The bar's button proxies clicks to the original inline button so all
 *      existing add-to-cart / voucher / calendar logic just works.
 *
 * Why JS-only (no template changes besides one <script> tag):
 *   - The product page has many product-type variants (delivery, voucher,
 *     calendar, retail, etc.) each with slightly different CTAs.
 *   - Doing the detection at runtime means we adapt to whichever CTA is
 *     actually rendered, without forking the templates.
 *
 * Activation: only runs on body.template-product, only ≤749px viewports.
 * -------------------------------------------------------------------------- */

(function () {
  'use strict';

  // De-dupe guard: if the script is included by multiple snippets (e.g.
  // product-form.liquid is rendered more than once per page), only run once.
  if (window.__wdStickyCtaInited) return;
  window.__wdStickyCtaInited = true;

  // Bail early on non-product pages.
  if (!document.body || !document.body.classList.contains('template-product')) {
    return;
  }

  // Bail early on desktop — the right column is already sticky there.
  var MOBILE_MAX = 749;
  if (window.matchMedia('(min-width: ' + (MOBILE_MAX + 1) + 'px)').matches) {
    return;
  }

  var bar = null;
  var observer = null;
  var inlineBtn = null;

  // Selectors to try, in order. First match wins.
  // Covers the various CTA buttons across product variants.
  var BTN_SELECTORS = [
    '.buyBtnForm',         // generic add-to-cart in product-form.liquid
    '.deliveryBuyBtn',     // delivery-flow variant
    '.atc-button',         // pixel-union variant
    '[type="submit"][name="add"]', // standard Shopify add-to-cart submit
  ];

  function findInlineBtn() {
    for (var i = 0; i < BTN_SELECTORS.length; i++) {
      var el = document.querySelector(BTN_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // Build the sticky bar markup once. Re-queries price each time it's shown
  // in case the user changed a variant.
  function buildBar() {
    var el = document.createElement('div');
    el.className = 'wd-sticky-cta';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Quick purchase');
    el.innerHTML =
      '<div class="wd-sticky-cta__price">' +
        '<span class="wd-sticky-cta__price-current" data-wd-cta-current></span>' +
        '<span class="wd-sticky-cta__price-old" data-wd-cta-old></span>' +
        '<span class="wd-sticky-cta__price-code" data-wd-cta-code style="display:none;font-size:11px;font-weight:600;line-height:1.2;"></span>' +
      '</div>' +
      '<button type="button" class="wd-sticky-cta__btn" data-wd-cta-btn ' +
        'data-track-section="pdp_main" data-track-section-label="Product page" ' +
        'data-track-item="sticky_buy" data-track-label="Sticky buy bar">' +
        'Buy now' +
      '</button>';
    document.body.appendChild(el);

    // Proxy click → trigger the inline button directly (no page scroll).
    // Forwarding the click runs all existing add-to-cart / voucher / calendar
    // logic, including any "select a date" validation notifications, without
    // yanking the viewport back up to the inline button.
    el.querySelector('[data-wd-cta-btn]').addEventListener('click', function (e) {
      e.preventDefault();
      var btn = e.currentTarget;
      if (!inlineBtn) return;
      // Tap feedback: spinner alongside the unchanged "Buy now" label while the
      // cart request / redirect runs.
      btn.classList.add('is-loading');
      inlineBtn.click();
      // Safety reset if no navigation happens (e.g. validation blocked the add).
      setTimeout(function () {
        btn.classList.remove('is-loading');
      }, 4000);
    });

    return el;
  }

  // Sync the price text on the bar from the visible price block.
  function syncPrice() {
    if (!bar) return;
    var currentEl = bar.querySelector('[data-wd-cta-current]');
    var oldEl = bar.querySelector('[data-wd-cta-old]');
    if (!currentEl || !oldEl) return;

    // Current price — try a few selectors (different per template).
    var currentSrc =
      document.querySelector('.salary-font') ||
      document.querySelector('.price__current .money') ||
      document.querySelector('.price__current');
    var oldSrc =
      document.querySelector('.old-price') ||
      document.querySelector('.price__compare-at .money') ||
      document.querySelector('.price__compare-at');

    // The promo-price scripts replace the source's text with a .money span
    // plus a "with code X" sub-line. Copy just the price from the .money
    // span (raw textContent would flatten both into one big string) and
    // surface the code on its own small line.
    var moneySub = currentSrc ? currentSrc.querySelector('.money') : null;
    currentEl.textContent = moneySub
      ? moneySub.textContent.trim()
      : (currentSrc ? currentSrc.textContent.trim() : '');
    var codeEl = bar.querySelector('[data-wd-cta-code]');
    if (codeEl) {
      var promoStrip = document.querySelector('.wd-promo-strip');
      var promoCode = promoStrip && promoStrip.getAttribute('data-wd-promo-code');
      if (moneySub && promoCode) {
        codeEl.textContent = 'with code ' + promoCode;
        codeEl.style.display = 'block';
      } else {
        codeEl.style.display = 'none';
      }
    }
    if (oldSrc && oldSrc.textContent.trim()) {
      oldEl.textContent = oldSrc.textContent.trim();
      oldEl.style.display = '';
    } else {
      oldEl.style.display = 'none';
    }
  }

  function showBar() {
    if (!bar) return;
    syncPrice();
    bar.classList.add('wd-sticky-cta--visible');
    // Signal to other fixed UI (e.g. floating WhatsApp) to move clear of the bar.
    document.body.classList.add('wd-cta-visible');
  }
  function hideBar() {
    if (!bar) return;
    bar.classList.remove('wd-sticky-cta--visible');
    document.body.classList.remove('wd-cta-visible');
  }

  function init() {
    inlineBtn = findInlineBtn();
    if (!inlineBtn) {
      // CTA not in the DOM (e.g. sold-out product or unusual variant) — nothing to do.
      return;
    }

    bar = buildBar();

    // Show the bar when the inline button leaves the viewport, hide when it returns.
    // rootMargin of -10% keeps the bar from flickering near the edge.
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) hideBar();
        else showBar();
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });

    observer.observe(inlineBtn);

    // Keep the price in sync when variants change.
    // The theme dispatches a variant-change event; we listen broadly so we
    // catch whichever event fires.
    ['variant:change', 'product:variant-change', 'change'].forEach(function (evt) {
      document.addEventListener(evt, syncPrice, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}());
